import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Calendar, Users, Building2, Image as ImageIcon, BookOpen, Brain,
  FileText, Sparkles, LogOut, Crown, ChevronRight, Sparkle
} from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import LanguageToggle from "./LanguageToggle";
import AdminMobileNav from "./AdminMobileNav";
import AdminOpsBadge from "./AdminOpsBadge";
import { useAdminAuth } from "../auth/AdminAuthContext";

// Section groupings — mirrors AdminMobileNav order.
const NAV_GROUPS = [
  {
    label_vi: "Vận hành", label_en: "Operations",
    items: [
      { to: "/admin/nail-events", icon: Calendar, vi: "Sự kiện", en: "Events", match: ["/admin/nail-events"] },
    ],
  },
  {
    label_vi: "Thư viện", label_en: "Library",
    items: [
      { to: "/admin/library/speakers", icon: Users, vi: "Diễn giả", en: "Speakers" },
      { to: "/admin/board", icon: Crown, vi: "Hội đồng QT", en: "Board" },
      { to: "/admin/library/sponsors", icon: Building2, vi: "Tài trợ", en: "Sponsors" },
      { to: "/admin/library/media", icon: ImageIcon, vi: "Media", en: "Media" },
      { to: "/admin/library/knowledge", icon: BookOpen, vi: "Tri thức", en: "Knowledge" },
      { to: "/admin/library/articles", icon: FileText, vi: "Bài viết", en: "Articles" },
    ],
  },
  {
    label_vi: "AI & Huấn luyện", label_en: "AI & Training",
    items: [
      { to: "/admin/library/ai", icon: Brain, vi: "Bộ Não AI", en: "AI Brain" },
      { to: "/admin/training", icon: Sparkles, vi: "Huấn luyện", en: "Training" },
    ],
  },
];

function isActive(pathname, item) {
  const matchers = item.match || [item.to];
  return matchers.some((m) => pathname === m || pathname.startsWith(m + "/"));
}

/**
 * AdminShell — premium sidebar layout for the master admin dashboard.
 *
 * Props:
 *   title       (string | node)   page title (VI/EN handled by caller)
 *   subtitle    (string | node)   optional lead line above title (small uppercase)
 *   actions     (node)            right-side buttons on the top bar (e.g. "Add Speaker")
 *   backTo      (string)          optional href for a back-chevron in top bar
 *   backLabel   (string)          label for back-chevron
 *   testid      (string)          data-testid on outer container
 *   children    (node)            page body
 */
export default function AdminShell({
  title, subtitle, actions, backTo, backLabel,
  testid = "admin-shell", children,
}) {
  const { lang } = useI18n();
  const L = (vi, en) => (lang === "en" ? en : vi);
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-linen text-espresso flex" data-testid={testid}>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex lg:flex-col lg:w-64 xl:w-72 bg-espresso text-linen border-r border-espresso/40 fixed inset-y-0 left-0 z-30"
        data-testid="admin-shell-sidebar"
      >
        <Link to="/admin/nail-events" className="px-6 py-6 border-b border-white/10 block">
          <div className="flex items-center gap-2">
            <Sparkle className="w-4 h-4 text-[#E8C878]" />
            <p className="text-[10px] uppercase tracking-[0.32em] text-white/60 font-semibold">Master Admin</p>
          </div>
          <p className="font-serif text-xl mt-1.5 text-linen leading-tight">Nail Viet Club</p>
        </Link>

        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
          {NAV_GROUPS.map((group) => (
            <div key={group.label_vi}>
              <p className="px-3 mb-2 text-[10px] uppercase tracking-[0.24em] text-white/40 font-semibold">
                {L(group.label_vi, group.label_en)}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(location.pathname, item);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={`admin-shell-nav-${item.to.split("/").pop()}`}
                      className={
                        "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition " +
                        (active
                          ? "bg-[#E8C878]/15 text-[#F4E4C1] shadow-inner"
                          : "text-white/70 hover:text-linen hover:bg-white/5")
                      }
                    >
                      <Icon className={"w-4 h-4 " + (active ? "text-[#E8C878]" : "text-white/50")} />
                      <span className="truncate">{L(item.vi, item.en)}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 p-3">
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.24em] text-white/40 font-semibold">
              {L("Đăng nhập", "Signed in")}
            </p>
            <p className="text-sm text-white/90 truncate mt-0.5" data-testid="admin-shell-email">
              {admin?.email || "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-shell-logout"
            className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-linen rounded-xl px-3 py-2.5 text-sm font-medium transition"
          >
            <LogOut className="w-4 h-4" /> {L("Đăng xuất", "Sign out")}
          </button>
        </div>
      </aside>

      {/* Content column */}
      <div className="flex-1 lg:pl-64 xl:pl-72 min-w-0">
        {/* Top bar */}
        <header
          className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-espresso/10"
          data-testid="admin-shell-topbar"
        >
          <div className="px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-3">
            {/* Mobile hamburger */}
            <div className="lg:hidden">
              <AdminMobileNav lang={lang} />
            </div>

            {/* Title / breadcrumb */}
            <div className="min-w-0 flex-1">
              {backTo && (
                <Link
                  to={backTo}
                  className="text-espresso-muted hover:text-espresso inline-flex items-center gap-1 text-xs font-medium"
                  data-testid="admin-shell-back"
                >
                  <ChevronRight className="w-3.5 h-3.5 rotate-180" /> {backLabel || L("Quay lại", "Back")}
                </Link>
              )}
              {subtitle && (
                <p className="text-[10px] uppercase tracking-[0.28em] text-espresso-muted font-semibold">
                  {subtitle}
                </p>
              )}
              {title && (
                <h1 className="font-serif text-lg md:text-xl text-espresso leading-tight mt-0.5 truncate">
                  {title}
                </h1>
              )}
            </div>

            {/* Right controls */}
            <div className="flex items-center gap-2 shrink-0">
              <AdminOpsBadge />
              <LanguageToggle />
              {actions}
              <button
                type="button"
                onClick={onLogout}
                data-testid="admin-shell-topbar-logout"
                className="hidden lg:inline-flex items-center gap-1.5 text-espresso-muted hover:text-espresso text-xs font-medium px-2 py-1.5 rounded-full hover:bg-linen-100"
                title={L("Đăng xuất", "Sign out")}
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </header>

        {/* Body */}
        <main className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="admin-shell-main">
          {children}
        </main>
      </div>
    </div>
  );
}
